import type { TypeCmsProductDetail, TypeLang } from '@/schema'
import { memo, useCallback, useEffect, useMemo, type FC, type Dispatch, type SetStateAction, useState } from 'react'
import styled from 'styled-components'
import { InputRadio } from '../commons/inputs'
import { SubmitHandler, useForm, UseFormRegister } from 'react-hook-form'
import { fetchAPI } from '@/api'
import { useParams } from 'next/navigation'
import { useTranslator } from '@/hooks'
import { mq } from '@/styles'
import type { TypeCmsDownloadObj } from '../SectionConfigurator'

type Props = {
  data: TypeCmsProductDetail['configurator']['options']
  forceReset?: boolean
  setGeneratedCode: Dispatch<SetStateAction<string>>
  setSelectedModel: Dispatch<SetStateAction<string>>
  submitOnChange?: boolean
  setGeneratedDownload?: Dispatch<SetStateAction<TypeCmsDownloadObj>>
  initialModelSlug?: string
}

const checkIfModelHasDimmingOnOff = (data: Props['data'], modelSlug: string) => {
  const modelArr = data.find(item => item.type === 'model')
  if (!modelArr) return false

  const model = modelArr?.option_values.find(option => option.slug === modelSlug)
  if (!model) return false

  return model.on_off
}

const MODEL_SLUG_BY_LANG = {
  en: 'model',
  es: 'modelo',
  fr: 'modele',
  it: 'modello',
}

const isKeynameTheOnlyWithValue = (keyname: string, submitData: unknown): boolean => {
  // Check if the object exists and has properties
  if (!submitData || typeof submitData !== 'object') {
    return false
  }

  // Get all keys from the object
  const keys = Object.keys(submitData)

  // Check if the specified keyname exists in the object
  if (!keys.includes(keyname)) {
    return false
  }

  // Check if the specified keyname has a valid (non-null, non-undefined) value
  const keynameValue = submitData[keyname]
  const isKeynameValid = keynameValue !== null && keynameValue !== undefined && keynameValue !== ''

  if (!isKeynameValid) {
    return false
  }

  // Check that all other keys have invalid values (null, undefined, or empty string)
  const otherKeysAreInvalid = keys.every(key => {
    if (key === keyname) return true // Skip the target keyname

    const value = submitData[key]
    return value === null || value === undefined || value === ''
  })

  return otherKeysAreInvalid
}

const getFields = (data: Props['data'], register: UseFormRegister<unknown>, t, watchModel, setSelectedModel, initialModelSlug, disabledInputs, disabledInputsPerModel = null ) => data.map((item, idx) => {
  // All inputs are not required so we submit the form on every change
  // const required = item.type !== 'dimming' && item.type !== 'emergency'

  // at model level we have a boolean to check if the model has dimming on/off options
  const modelHasDimmingOnOff = checkIfModelHasDimmingOnOff(data, watchModel)

  return (
    <fieldset key={`${idx}-${item.id}`} className={item.type} data-disabled={disabledInputs?.[item.slug]?.allDisabled}>
      <legend>{item.name}</legend>
      {((item.type === 'dimming' && modelHasDimmingOnOff) || item.type === 'emergency') && <InputRadio key={0 + item.type} label={t(`project.product.configurator.no_${item.type}`) as string} name={item.slug} value={'no_aplica'} register={register} required={false} />}
      {item.option_values.map((option, index) => {
        const id = option.slug
        // const singleOption = (item.type !== 'dimming' && item.type !== 'emergency' && item.option_values?.length === 1)
        const defaultChecked = (item.type === 'model' && initialModelSlug === option.slug)
        // const defaultChecked = (item.type === 'model' && initialModelSlug === option.slug) || singleOption
        const isDisabled = item.type !== 'model' && disabledInputs?.[item.slug]?.[option.slug] || disabledInputs?.[item.slug]?.allDisabled

        // // Hide if model hasn't this option)
        const isDisabledPerModel = disabledInputsPerModel?.[item.slug]?.[option.slug] || disabledInputsPerModel?.[item.slug]?.allDisabled

        return <InputRadio key={`${index}-${option.id}`} label={option.name} name={item.slug} value={`${option.slug}`} register={register} required={false} id={id} {...item.type === 'model' && { onClick: () => { setSelectedModel(id) } }} {...defaultChecked && { defaultChecked: true }} disabled={isDisabled} dataHidden={isDisabledPerModel} />
      })}
    </fieldset>
  )
})

const getIds = (data: Props['data'], formData) => {
  const ids = []
  for (const keyname in formData) {
    const dataKeyObj = data.find(item => item.slug === keyname)
    const itemId = dataKeyObj?.id
    const valueId = dataKeyObj?.option_values.find(item => item.slug === formData[keyname])?.id

    if(itemId && valueId) ids.push(`${itemId}-${valueId}`)
  }

  return ids
}

export const FormConfigurator: FC<Props> = memo(({ data, submitOnChange = false, setSelectedModel, setGeneratedCode, forceReset, setGeneratedDownload, initialModelSlug }) => {
  const { lang, slug }: { lang: TypeLang, slug: string } = useParams()
  const { handleSubmit, register, watch, reset, setValue } = useForm()
  const { t } = useTranslator()
  const [initialSubmitDone, setInitialSubmitDone] = useState(false)


  // Disabled inputs base on choices selected
  const [disabledInputs, setDisabledInputs] = useState(null)
  // Disabled inputs per model
  const [disabledInputsPerModel, setDisabledInputsPerModel] = useState(null)

  const [loading, setLoading] = useState(false)
  const watchModel = watch(MODEL_SLUG_BY_LANG[lang])

  const Fields = useMemo(() => getFields(data ?? [], register, t, watchModel, setSelectedModel, initialModelSlug, disabledInputs, disabledInputsPerModel), [data, register, t, watchModel, setSelectedModel, initialModelSlug, disabledInputs, disabledInputsPerModel])

  const getDisabledInputs = useCallback((validData, rawPerModel = false) => {
    const incomingDisabled = {}

    if(!validData || Object.keys(validData)?.length < 1) {
      setDisabledInputs(null)
      return
    }

    data.forEach(item => {
      const validItem = validData.find(validItem => validItem.id === item.id)
      if(!validItem) incomingDisabled[item.slug] = { allDisabled: true }

      item.option_values.forEach(option => {
        const validOption = validItem?.option_values.find(validOption => validOption.id === option.id)
        if(!validOption) {
          incomingDisabled[item.slug] = { ...incomingDisabled[item.slug], [option.slug]: true }
        }
      })
    })

    if (rawPerModel) setDisabledInputsPerModel(Object.keys(incomingDisabled)?.length > 0 ? incomingDisabled : null)
    else setDisabledInputs(Object.keys(incomingDisabled)?.length > 0 ? incomingDisabled : null)
  }, [data, setDisabledInputsPerModel, setDisabledInputs])

  const onSubmit: SubmitHandler<unknown> = useCallback(async formData => {
    const ids = getIds(data, formData)

    // setJustModelSelected(ids.length === 1 && formData.model)

    if(!ids) return
    setGeneratedCode(t('project.product.configurator.loading') as string)


    try {
      setLoading(true)
      const res = await fetchAPI('get-product-configurator', { lang, slug, ids: ids.join(',') }, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }) as Response

      if(!res.ok) {
        setGeneratedCode(null)
        setGeneratedDownload?.(null)
        console.warn('🟡 Error fetching configurator data')
        return
      }

      const data = await res.json()
      const validData = Array.isArray(data) ? data : Array.isArray(data?.options) ? data?.options : null

      const onlyModelHasValue = isKeynameTheOnlyWithValue(MODEL_SLUG_BY_LANG[lang], formData)

      if (validData) getDisabledInputs(validData, onlyModelHasValue)

      if(data?.variant?.code) {
        setGeneratedCode(data.variant.code)
        setGeneratedDownload?.(data.variant?.download)
      }
      else {
        setGeneratedCode(null)
        setGeneratedDownload?.(null)
      }

      setLoading(false)
    } catch (e) {
      setGeneratedCode(null)
      setGeneratedDownload?.(null)
      setLoading(false)
      console.warn('🟡 Catch Error fetching configurator data', e)
    }
  }, [data, setGeneratedCode, t, lang, slug, getDisabledInputs, setGeneratedDownload])

  const onError = (errors, e) => {
    console.warn('🟡 Errors found before Submit')
    console.warn(errors, e)
  }

  const restoreFirstModel = useCallback(() => {
    const firstModel = data.find(item => item.type === 'model')
    const keyname = firstModel?.slug
    const value = initialModelSlug
    const modelIdState = value

    if (keyname && value) {
      setValue(keyname, value)
      setSelectedModel(modelIdState)
    }
  }, [data, setSelectedModel, setValue, initialModelSlug])

  const resetAllInputsExceptModel = useCallback(() => {
    for(const item of data) {
      if(item.type !== 'model') {
        setValue(item.slug, null)
      }
    }
  }, [data, setValue])

  // Auto submit on change
  useEffect(() => {
    if (!submitOnChange) return

    const subscription = watch(() => {
      handleSubmit(onSubmit)()
    })
    return () => subscription.unsubscribe()
  }, [submitOnChange, watch, handleSubmit, onSubmit])

  useEffect(() => {
    if (forceReset) {
      reset()
      restoreFirstModel()
      setGeneratedDownload(null)
      setGeneratedCode(null)
      setLoading(false)
      setDisabledInputs(null)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceReset])

  useEffect(() => {
    if (watchModel) {
      resetAllInputsExceptModel()

      async function getDisabledOptionsValuesPerModel() {
        const idsArr = getIds(data, { model: watchModel })
        const id = idsArr?.[0]
        if(!id) return
        const res = await fetchAPI('get-product-configurator', { lang, slug, ids: id }, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }) as Response

        if(!res.ok) return
        const resData = await res.json()
        const validData = Array.isArray(resData) ? resData : Array.isArray(resData?.options) ? resData?.options : null
        if (validData) getDisabledInputs(validData, true)
      }

      getDisabledOptionsValuesPerModel()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchModel])

  useEffect(() => {
    if (initialModelSlug && !initialSubmitDone) {
      handleSubmit(onSubmit)()
      setInitialSubmitDone(true)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialModelSlug, initialSubmitDone])

  return (
    <Form onSubmit={handleSubmit(onSubmit, onError)} aria-disabled={loading}>
      {Fields}
    </Form>
  )
})

const Form = styled.form`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-column-gap: 20px;
  grid-row-gap: 40px;
  margin-bottom: 30px;
  opacity: 1;
  transition: opacity 250ms ease-in-out;
  /* align-items: flex-start;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 20px;
  justify-content: flex-start; */

  ${mq.greaterThan('tablet')} {
    display: grid;
    grid-template-columns: repeat(6, 2fr);
    grid-column-gap: 30px;
    /* align-items: flex-start;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 40px;
    justify-content: flex-start; */
    margin-bottom: 80px;
  }

  @keyframes flash {
    from { opacity: 1; }
    to { opacity: .4; }
  }

  &[aria-disabled='true'] {
    animation: flash 500ms ease infinite alternate;
    pointer-events: none;
  }

  legend {
    border: none;
    margin-bottom: 15px;
    opacity: .5;
  }

  fieldset {
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    justify-content: flex-start;

    &[data-disabled='true'] {
      display: none;
    }

    &.model {
      height: 0;
      overflow: hidden;
      position: absolute;
      visibility: hidden;
      width: 0;
    }

    label {
      margin-bottom: 2px;
      padding: 0;

      > div {
        height: 10px;
        margin-top: 8px;
        width: 10px;
      }
    }
  }

  input {
    &[type='radio'] {
      border-bottom: none !important;
    }
  }
`
